/* eslint-disable react/jsx-no-constructed-context-values */
// ^^^ eslint rule explanation below as to why we can't memoize it in this one case.

/*  iceberg-ui  */
import { useTableV2 } from '@auviknetworks/iceberg-ui';
import {
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel
} from '@tanstack/react-table';
import 'application/Shared/Components/Table/helpers';

/*  Shared  */
import { Helpers } from 'application/Shared/Helpers';
import { DataAPI } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { usePrevious } from 'hooks/usePrevious';

/* NPM */
import { cloneDeep } from 'lodash';

/*  React  */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
    filterChangeHandler,
    filterRowData,
    filtersToParameters,
    useGetTableViewFromURL,
    useHasViewBeenModified,
    usePutTableStateInURL,
    useTableStateFromURL,
    useUpdateCurrentView,
    useUpdateTableState
} from './utils';

/*  Constants  */
const { isset } = Helpers;

const VALID_ROWS_PER_PAGE = [-1, 2, 5, 6, 10, 25, 50, 100];

// When passing in rowsPerPage as -1, this indicates that we want to show all rows.
// However, when -1 is passed to the initialState as pageSize, it seems to render all rows - 1 row.
// So, we set the max page size to 100,000 whenever rowsPerPage is -1.
const MAX_ROWS_PER_PAGE = 100_000;

const getParameters = ({ clientId, currentDataAPI, parameters, toggles }) => {
    if (clientId) {
        currentDataAPI.addParameter('platformTenantId', clientId);
        currentDataAPI.addParameter('includeClients', true);
    }

    // For all the toggled checkboxes, add their parameters to the api call
    Object.values(toggles).forEach((toggle) => {
        const { enabled, forceParameter, parameter } = toggle;
        if (enabled || forceParameter) {
            const { field, value } = parameter;
            const forcedValue = enabled ? value : !value;
            currentDataAPI.addParameter(field, forcedValue);
        }
    });

    // If there are any parameters add those last.
    if (Helpers.isObject(parameters)) {
        Object.keys(parameters).forEach((field) => {
            const value = parameters[field];
            currentDataAPI.addParameter(field, value);
        });
    }

    // Get the built config and return the parameters
    return currentDataAPI.getConfig().parameters;
};

/**
 * Creates a toggles object from an array of toggle configurations
 * @param {Array<import('./types').Toggle>} propsToggles - Array of toggle configurations
 * @returns {import('./types').ToggleState} Object mapping toggle keys to their configurations with enabled state
 */
const makeToggles = (propsToggles) => {
    const currentToggles = {};

    if (propsToggles?.length) {
        propsToggles.forEach((toggle) => {
            const { key, defaultEnabled } = toggle;
            currentToggles[key] = { ...toggle, enabled: defaultEnabled };
        });
    }

    return currentToggles;
};

const setupColSpan = ({ columns }) => {
    let colSpan = 0;

    columns?.forEach((column) => {
        const visibleColumn = column.getIsVisible();
        colSpan += visibleColumn ? 1 : 0;
    });

    return colSpan;
};

const setupDataAPI = ({
    apiSize,
    clientId,
    dataAPI,
    parameters,
    rowsApi,
    rowsApiPreload,
    rowsPerPage,
    sortState,
    toggles
}) => {
    // If we are setting up dataAPI, we need to clean up the previous parameters
    dataAPI.resetParams();

    /* Get any query parameters that may have been sent with the route
        '/{api}/?enabled=true' and add them as values to dataAPI */
    const params = Helpers.getQueryParamsFromString(rowsApi);
    dataAPI.addParameters(params);

    const newParams = getParameters({
        clientId,
        currentDataAPI: dataAPI,
        parameters,
        toggles
    });
    dataAPI.addParameters(newParams);

    // If not doing pagination, set the size to -1
    if (rowsApiPreload) {
        dataAPI.addParameter('size', apiSize || -1);
    } else {
        dataAPI.addParameters({
            order: sortState.direction,
            size: rowsPerPage,
            sort: sortState.key
        });
    }
};

const queryCountApi = async ({ countAPI, dataAPI }) => {
    // countAPI's params need to be in sync with dataAPI's
    // eslint-disable-next-line no-param-reassign
    countAPI.config = dataAPI.getConfig();
    // take the original queries config, then add count to true
    countAPI.addParameter('count', true);

    // get the response and assign the count
    const rowCountRes = await countAPI.getResults();
    const currentRowCount = isset(rowCountRes, 'count')
        ? parseInt(rowCountRes.count, 10)
        : 0;

    return currentRowCount;
};

export const ManagedTableV2Context = createContext({});

/**
 * Returns provider values that any table needs to use.
 *
 *  @param {object} props
 *  @param {number} [props.apiSize] Number of records to retrieve from the API.
 *  @param {object} [props.baseParameters] Object that has the base/initial parameters for the API call. They can be augmented by filters and toggles.
 *  @param {ReactElement} [props.bulkActions] JSX component of buttons that go next to the export button.
 *  @param {ReactNode} props.children child elements to be rendered
 *  @param {object} props.columns Array of the table columns. They need to be in the structure tanstack react-table needs: https://tanstack.com/table/v8/docs/api/core/column-def
 *  @param {object} [props.customTableViews] Object with the definitions of all the user's custom table views available. Will add a button to be able to select and save table views.
 *  @param {string} [props.defaultSearch] Initial search string, if provided.
 *  @param {boolean} [props.enableColumnFilters] Enable column filters or not.
 * @param {boolean} [props.enableMultisort] Enable sorting by multiple columns by pressing shift down while clicking on the sorting caret of a different column.
 *  @param {boolean} [props.enableSorting] Enable sorting or not.
 *  @param {boolean} [props.enablePagination] Enable pagination or not.
 *  @param {boolean} [props.externalLoading] Are we still loading the data in the component using ManagedTable?
 *  @param {function} [props.handleRows] Function for when we pre-process rowMap information additionally in the component itself, not here, usually for csv exports.
 *  @param {boolean} [props.ignoreSearchParams] Flag to disable updating the search params when the table state changes.
 *  @param {object} [props.initialState] Initial column pinning and visibility states
 *  @param {string} [props.mode] 'client' or 'hybrid'. If 'client', the table will do all the filtering and sorting on the client side. If 'hybrid', it will use the API to do the filtering and sorting.
 *  @param {function} [props.preprocessRows] Function to add properties to the rows object, not here, but in the component.
 *  @param {string} [props.getRowId] data parameter that is being used as a row's key by react-table. It should be unique per row.
 *  @param {function} [props.pollingFn] Function to call when polling for data.
 *  @param {number} [props.pollingInterval] Function to call when polling for data.
 *  @param {function} [props.refetchCustomTableViews] Function to call the userCustomTableView endpoint again.
 *  @param {boolean} [props.resetRowSelection] Whether we need clear the selected rows in the table.
 *  @param {object} [props.rows] If given, we won't be using the API to get the data, since rows is the data we want to display.
 *  @param {string} [props.rowsApi] API endpoint to call to get the data.
 *  @param {boolean} [props.rowsApiPreload] Load all the data at once, if false, number of records to retrieve is rowsPerPage.
 *  @param {number} [props.rowsPerPage] Initial rows per page.
 *  @param {boolean} [props.startPolling] Start polling for data
 *  @param {boolean} [props.searchEnabled] Show search bar or not.
 *  @param {function} [props.setResetRowSelection] Trigger to clear the selected rows in the table.
 *  @param {boolean} [props.showAutoupdate] Whether we will call make the same API call every 30s, refresh the data.
 *  @param {string} [props.sortDirection] ASC/DESC. Initial sort direction.
 *  @param {string} [props.sortKey] Initial id of the column to sort.
 *  @param {string} [props.tableName] the name of the table, used for the URL state.
 *  @param {object} [props.tableViews] Object with the definitions of all the table views available. Will add a button to be able to select and save table views.
 *  @param {import('./types').Toggle[]} [props.toggles] Initial toggles per page, they add parameters to the API call.
 *  @param {function} [props.setToggles] Function to set the toggles.
 *
 *  @returns {ReactNode} a context provider with numerous provider values.
 *
 * @example
 *      <ManagedTableV2ContextProvider
 *           baseParameters={baseParameters}
 *           columns={columns}
 *           rowsApi="/tenantApp"
 *           rowsApiPreload
 *           rowsPerPage={10}
 *           searchEnabled={false}
 *           sortDirection="DESC"
 *           sortKey="dateCreated"
 *       >
 *           <Paper elevation={0} variant="outlined" sx={{ p: 0 }}>
 *               <ManagedTableV2UI
 *                   renderFooter
 *                   titleDef={{
                        noCount: true,
                        titleKey: 'Common.applications'
                     }}
 *               />
 *           </Paper>
 *       </ManagedTableV2ContextProvider>
 *
 */
export const ManagedTableV2ContextProvider = ({
    apiSize,
    baseParameters,
    bulkActions,
    // className, // Used in two places for `squeezedTable`
    columns = [],
    customTableViews: propsCustomTableViews,
    defaultSearch,
    enableColumnFilters = true,
    enableMultiSort = true,
    enableSorting = true,
    enablePagination = true,
    externalLoading,
    getRowId = 'id',
    handleRows,
    ignoreSearchParams,
    initialState = {},
    mode = 'hybrid',
    preprocessRows,
    refetchCustomTableViews,
    resetRowSelection,
    rows,
    rowsApi,
    rowsApiPreload,
    rowsPerPage: propsRowsPerPage,
    pollingFn,
    pollingInterval,
    searchEnabled,
    setResetRowSelection,
    showAutoupdate,
    sortDirection: propsSortDirection,
    sortKey: propsSortKey,
    startPolling,
    tableName,
    tableViews: propsTableViews,
    toggles: propsToggles,
    setToggles: propsSetToggles,
    children
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();

    const [apiLoading, setApiLoading] = useState(false);
    const apiTable = !!rowsApi;
    const [autoupdate, setAutoupdate] = useState(
        showAutoupdate || startPolling
    );
    const [showFlyoutMenu, setShowFlyoutMenu] = useState('hide');
    const [rerender, setRerender] = useState(false);
    const [rowCount, setRowCount] = useState(rows?.length || 0);
    const [rowData, setRowData] = useState(
        !rowsApi && rows?.length ? [...rows] : null
    );

    const rowsPerPage = useMemo(
        () =>
            VALID_ROWS_PER_PAGE.includes(propsRowsPerPage)
                ? propsRowsPerPage
                : 10,
        [propsRowsPerPage]
    );
    const [searchText, setSearchText] = useState(
        defaultSearch ? defaultSearch.toLowerCase() : ''
    );

    const [clientSideFilters, setClientSideFilters] = useState({});

    const sortState = useMemo(
        () => ({
            direction: propsSortDirection || 'DESC',
            key: propsSortKey
        }),
        [propsSortDirection, propsSortKey]
    );

    // We try to get the columnPinning from the initialState.
    const { columnPinning } = initialState;
    const initialColumnOrder = columns
        .filter(({ id }) =>
            columnPinning
                ? ![...columnPinning.left, ...columnPinning.right].includes(id)
                : true
        )
        .map(({ id }) => id);

    // We are setting the columnFilters.
    const [columnFilters, setColumnFilters] = useState(
        initialState?.columnFilters || []
    );

    const [toggles, setToggles] = useState(makeToggles(propsToggles));

    // rowsApi?.split is used to take the main part of the route.
    // If any parameters are added after the `?`, they will be ignored.
    // If parameters are needed, they need to be added to the parameters object instead.
    const dataAPI = useRef(
        rowsApi
            ? new DataAPI({
                  route: rowsApi?.split('?')[0]
              })
            : null
    );

    const countAPI = useRef(
        rowsApi ? new DataAPI(dataAPI?.current?.getConfig()) : null
    );
    const autoupdateInterval = useRef(null);
    const handleSwitchChange = () => {
        setAutoupdate(!autoupdate);
    };

    const cancelDataApis = useCallback(
        ({ cancelDataApi = false, cancelCountApi = false }) => {
            if (cancelDataApi && dataAPI.current) {
                dataAPI.current.cancel();
            }
            if (cancelCountApi && countAPI.current) {
                countAPI.current.cancel();
            }
        },
        []
    );

    const [parameters, setParameters] = useState({
        ...baseParameters,
        // We need to merge the initial filters with the base parameters.
        ...filtersToParameters(columnFilters)
    });

    const [filters, setFilters] = useState({});

    /**
     * Our internal data structure post global Filtering
     */
    const [data, setData] = useState([]);
    const { prev } = usePrevious(rowData);

    // We filter the rows manually if we have a search string
    useEffect(() => {
        if (rowData) {
            // Update the table with filteredData
            setData(
                // If we are in client mode, we will leave the data filtering to Tanstack.
                // If we are in hybrid mode, we will filter the data here.
                mode !== 'client'
                    ? filterRowData({
                          clientSideFilters,
                          columns,
                          rowData,
                          searchText
                      })
                    : rowData
            );
        }
    }, [rowData, columns, searchText, clientSideFilters, mode]);

    const getRowIdFn = (originalRow) => {
        if (!isset(originalRow, getRowId)) {
            debug.warn(
                'provided getRowId value does not exist on originalRow',
                getRowId
            );
        }

        return originalRow[getRowId];
    };

    // Group everything that goes into the table initial state
    const tableInitialState = useMemo(
        () => ({
            columnFilters,
            columnOrder: initialColumnOrder,
            columnVisibility: initialState.columnVisibility || {},
            ...(columnPinning !== undefined ? { columnPinning } : {}),
            pagination: {
                pageSize: rowsPerPage === -1 ? MAX_ROWS_PER_PAGE : rowsPerPage
            },
            ...(sortState.key
                ? {
                      sorting: [
                          {
                              desc: sortState.direction === 'DESC',
                              id: sortState.key
                          }
                      ]
                  }
                : {})
        }),

        [
            columnFilters,
            columnPinning,
            initialColumnOrder,
            initialState.columnVisibility,
            rowsPerPage,
            sortState.direction,
            sortState.key
        ]
    );

    const table = useTableV2({
        // We want the table's pagination to reset on filter changes.
        autoResetPageIndex: true,
        columnResizeMode: 'onChange',
        columns,
        data: data || [],
        enableColumnFilters,
        enableColumnPinning: true,
        enableGlobalFilter: searchEnabled,
        enableMultiSort,
        enablePagination,
        enableSorting,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFilteredRowModel: getFilteredRowModel(),
        getRowId: getRowIdFn,
        // The initialState is the one that is used when running the table
        // `resetVisibility`, and `resetOrder` methods.
        initialState: tableInitialState,
        manualFiltering: mode !== 'client',
        // This onColumnFiltersChange function will be called with an updaterFn when state.columnFilters changes.
        // This overrides the default internal state management, so you will need to persist the state change either fully or partially outside of the table.
        // We apply it only in hybrid mode, as in client mode, the table will handle the filtering.
        ...(mode !== 'client'
            ? {
                  onColumnFiltersChange: (filterFn) =>
                      filterChangeHandler({
                          baseParameters,
                          currentColumnFilters: columnFilters,
                          filterFn,
                          setClientSideFilters,
                          setColumnFilters,
                          setParameters,
                          table
                      })
              }
            : {}),
        onGlobalFilterChange: setSearchText,
        state: {
            // The state of the table is managed by the table itself.
            globalFilter: searchText,
            // We apply the manually handled filters in hybrid mode.
            ...(mode !== 'client' ? { columnFilters } : {})
        },
        toggleAllRowsSelected: true
    });

    // Augment both tableViews and customTableViews to full - tanstack + our definitions -  state
    // Needed to cloneDeep otherwise changes to views affect the initial object and can't get back the original.
    const tempPropsTableViews = cloneDeep(propsTableViews);
    const tableViews = useMemo(
        () =>
            tempPropsTableViews?.map((view) => ({
                ...view,
                state: {
                    ...table.initialState,
                    ...view.state
                }
            })),
        [table.initialState, tempPropsTableViews]
    );

    let tempPropsCustomTableViews = cloneDeep(propsCustomTableViews);

    // This code block handles custom table view configurations:
    // 1. Iterates through each custom table view
    // 2. For views that have a columnOrder configuration:
    //    - Finds columns that exist in initialColumnOrder but not in the view's columnOrder
    //    - Appends these missing columns to the view's columnOrder
    //    - Sets visibility to false for these appended columns
    //    - This ensures all columns are accounted for while preserving the original view layout
    tempPropsCustomTableViews = tempPropsCustomTableViews?.map((view) => {
        const tempView = { ...view };

        if (view.config?.columnOrder) {
            const diff = initialColumnOrder.filter(
                (x) => !view.config.columnOrder.includes(x)
            );

            if (diff.length) {
                tempView.config.columnOrder = [
                    ...view.config.columnOrder,
                    ...diff
                ];
                diff.forEach((x) => {
                    tempView.config.columnVisibility[x] = false;
                });
            }
        }
        return tempView;
    });

    const customTableViews = useMemo(
        () =>
            tempPropsCustomTableViews?.map((view) => ({
                ...view,
                state: {
                    ...table.initialState,
                    ...view.config
                }
            })),
        [table.initialState, tempPropsCustomTableViews]
    );

    // Find the default view from tableViews
    const defaultView = tableViews?.find(
        (view) => view.displayName === t('TableViews.default')
    );

    // Augment it to make it a full table state
    if (defaultView) {
        defaultView.state = {
            ...table.initialState,
            ...defaultView?.state
        };
    }

    // Set the currentTableView, default to start with, if any
    const [currentTableView, setCurrentTableView] = useState(defaultView);

    const [viewHasBeenModified, setViewHasBeenModified] = useState(false);

    // We need to augment any view to make it a full tanstack table state.
    // Every time we reset the current table view, we need to clear the viewHasBeenModified flag.
    const newSetCurrentTableView = (view) => {
        setCurrentTableView(view);
        setViewHasBeenModified(false);
    };

    // Hook that runs on every table render and will reflect table `columnVisibility`, `columnOrder`, `columnFilters`, and
    // `tableView` to the URL if any of them changed.
    usePutTableStateInURL({
        ignoreSearchParams,
        table,
        tableName,
        tableViewID: currentTableView?.id
    });

    // Hooks that will try to retrieve the `columnVisibility`, `columnOrder`, `columnFilters`, and `tableView` name from the URL
    // and set it to the table. If the URL doesn't have the state, it will not change the table state. Keeps the first
    // value in the internal state, so it won't update the table state after it is set once.
    const stateFromURL = useTableStateFromURL(tableName);
    const tableViewIdFromUrl = useGetTableViewFromURL(tableName);

    const tableViewFromURL = () => {
        let tableView;
        if (tableViewIdFromUrl) {
            tableView = tableViews?.find(
                (view) => view.id === tableViewIdFromUrl
            );
            if (!tableView) {
                tableView = customTableViews?.find(
                    (view) => view.id === tableViewIdFromUrl
                );
            }
        }
        return tableView;
    };

    // Hook that will update the currentTableView state only when tableViewFromURL exists and
    // when we go into that url directly.
    useUpdateCurrentView({
        ignoreSearchParams,
        setCurrentTableView,
        stateFromURL,
        tableView: tableViewFromURL()
    });

    // Hook that will update the table state when the current tableView is changed.
    useUpdateTableState({
        ignoreSearchParams,
        state: currentTableView?.state,
        table
    });

    // Hook that will find if the currentTableView has changed.
    useHasViewBeenModified({
        currentTableView: tableViewFromURL(),
        ignoreSearchParams,
        setViewHasBeenModified,
        table
    });

    const colSpan = setupColSpan({ columns: table.getAllColumns() });

    const handleRowApi = useCallback(
        async ({ countUnaffected = false }) => {
            if (!rowsApi) {
                debug.i('rowsApi is undefined', rowsApi);
                return;
            }

            if (!dataAPI.current) {
                debug.i('dataAPI has not been initialized');
                return;
            }

            // Ignore setting the api loading when we are polling because it has a weird UX
            if (!startPolling) {
                setApiLoading(true);
            }

            cancelDataApis({
                cancelCountApi: !countUnaffected,
                cancelDataApi: !!apiTable
            }); // cancel countAPI only if count is affected

            setupDataAPI({
                apiSize,
                clientId,
                dataAPI: dataAPI.current,
                parameters,
                rowsApi,
                rowsApiPreload,
                rowsPerPage,
                sortState,
                toggles
            });

            debug.dev('TABLE API', rowsApi, dataAPI.current.getParams());

            let currentRowData = await dataAPI.current.getResults();

            // Don't set the rowCount if we are getting one page at a time from the backend
            if (rowsApiPreload) {
                setRowCount(Helpers.count(currentRowData));
            }

            if (handleRows) {
                handleRows(currentRowData);
            }

            if (preprocessRows) {
                currentRowData = preprocessRows(currentRowData);
            }

            /* If we're not doing pagination and api searching, then go ahead
            and just set apiTable to false so it can use local search */
            if (!rowsApiPreload && !countUnaffected && dataAPI.current) {
                const currentRowCount = await queryCountApi({
                    countAPI: countAPI.current,
                    dataAPI: dataAPI.current
                });
                setRowCount(currentRowCount);
            }

            // status is only set to canceled or error in DataAPI.js
            if (
                currentRowData?.status !== 'error' &&
                currentRowData?.status !== 'canceled'
            ) {
                setRowData(currentRowData);
            }

            // Don't leave the skeleton rows hanging
            if (currentRowData?.status === 'error' && !rowData) {
                setRowData([]);
            }

            setApiLoading(false);
        },
        [
            apiSize,
            apiTable,
            cancelDataApis,
            clientId,
            handleRows,
            parameters,
            preprocessRows,
            rowData,
            rowsApi,
            rowsApiPreload,
            rowsPerPage,
            startPolling,
            sortState,
            toggles
        ]
    );

    useEffect(() => {
        if (startPolling === true) {
            setAutoupdate(true);
        } else if (startPolling === false) {
            setAutoupdate(false);
        }
    }, [startPolling]);

    useEffect(() => {
        if (autoupdate) {
            autoupdateInterval.current = setInterval(() => {
                if (apiTable) {
                    handleRowApi({
                        countUnaffected: false // Total row count unaffected by filters change
                    });
                } else {
                    pollingFn(prev, rowData);
                }
            }, pollingInterval || 30000);

            // When passing in rowData manually and the data has changed,
            // we want to call the pollingFn function
            if (!apiTable && !Helpers.arraysEqual(prev, rowData)) {
                pollingFn(prev, rowData);
            }
        }

        // clear on component unmount
        return () => {
            clearInterval(autoupdateInterval.current);
        };
    }, [
        apiTable,
        autoupdate,
        handleRowApi,
        startPolling,
        pollingInterval,
        pollingFn,
        prev,
        rowData
    ]);

    // For the tables that do not use an endpoint to gather data, but rather, the data is passed on to the table directly.
    useEffect(() => {
        if (rows && !rowsApi) {
            setRowData(rows);
            setRowCount(rows.length);
        }
    }, [rows, rowsApi]);

    // If any parameters, toggles, or endpoint change, then call dataAPI again with new params.
    useEffect(() => {
        if (!dataAPI.current && !!rowsApi) {
            dataAPI.current = new DataAPI({
                route: rowsApi?.split('?')[0]
            });
            countAPI.current = rowsApi
                ? new DataAPI(dataAPI?.current?.getConfig())
                : null;
        }
        if (apiTable || Object.keys(toggles)?.length > 0) {
            handleRowApi({
                countUnaffected: false // Total row count unaffected by filters change
            });
            if (propsSetToggles) {
                propsSetToggles(toggles);
            }
        }

        setSearchText(defaultSearch ? defaultSearch.toLowerCase() : ''); // Reset search
        table.setPageIndex(0);
        table.resetRowSelection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parameters, rowsApi, toggles]);

    // If we did not download the data in one go (!rowsApiPreload), then when we change rowsPerPage, or the sort state, we need to make a new API call.
    useEffect(() => {
        if (apiTable && !rowsApiPreload) {
            handleRowApi({ countUnaffected: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsPerPage, sortState]);

    useEffect(() => {
        if (rerender && apiTable) {
            handleRowApi({ countUnaffected: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (resetRowSelection) {
            table.resetRowSelection();
            setResetRowSelection(false);
        }
    }, [resetRowSelection, setResetRowSelection, table]);

    useEffect(() => {
        if (rerender) {
            setRerender(false);
        }
    }, [rerender, setRerender]);

    // Add useEffect for initial table setup
    useEffect(() => {
        if (table) {
            // Reset page index after component mount
            table.resetPageIndex();
        }
    }, [table]);

    // Can't memoize, table is a stable reference, so we're passing the same value every time.
    // Since it's the same value, the context never will update.: https://github.com/TanStack/table/issues/2344
    // The simplest and the most effective solution to avoid context re-rendering is to create a wrapper for the context provider
    // and render its children's using children prop:
    // https://medium.com/@thebishalniroula/the-problem-with-context-api-and-how-to-solve-it-with-children-fc41eab9925f
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const providerValues = {
        apiLoading,
        apiTable,
        autoupdate,
        bulkActions,
        colSpan,
        currentTableView,
        customTableViews,
        data, // Filtered data
        dataAPI: dataAPI.current,
        defaultView,
        externalLoading,
        filters,
        handleSwitchChange,
        parameters,
        refetchCustomTableViews,
        rerender,
        rowCount,
        rowData, // data from API
        rowsApi,
        rowsPerPage,
        searchEnabled,
        searchText,
        setCurrentTableView: newSetCurrentTableView,
        setFilters,
        setParameters,
        setRerender,
        setSearchText,
        setShowFlyoutMenu,
        setToggles,
        setViewHasBeenModified,
        showAutoupdate,
        showFlyoutMenu,
        startPolling,
        t,
        table,
        tableName,
        tableViews,
        toggles,
        viewHasBeenModified
    };

    return (
        <ManagedTableV2Context.Provider value={providerValues}>
            {children}
        </ManagedTableV2Context.Provider>
    );
};

/**
 * Custom hook to access the ManagedTableV2 context
 * @template T - The type of data in the rows
 * @throws {Error} If used outside of ManagedTableV2ContextProvider
 * @returns {(
 *      import('./types').BaseTableContext<T> |
 *      import('./types').ApiTableContext<T> |
 *      import('./types').CustomTableContext<T> |
 *      import('./types').CSVTableContext<T>
 * )} The table context containing rowData and other properties
 */
export const useManagedTableV2Context = () => {
    const context = useContext(ManagedTableV2Context);

    if (context === undefined) {
        throw new Error(
            '`useManagedTableV2Context` must be used within `ManagedTableV2ContextProvider`'
        );
    }

    return context;
};
